import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Label } from "../ui/Label";
import { SuccessMessage } from "../ui/SuccessMessage";
import { ErrorMessage } from "../ui/ErrorMessage";
import { EyeOpenIcon, EyeClosedIcon } from "@radix-ui/react-icons";
import { httpService } from "../../services/http.service";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ChangePasswordSchemaType = z.infer<typeof changePasswordSchema>;

export const ChangePassword: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
  } = useForm<ChangePasswordSchemaType>({
    resolver: zodResolver(changePasswordSchema),
    mode: "onChange",
  });

  const currentPassword = watch("currentPassword");
  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  const isFormValid = !!currentPassword && !!newPassword && !!confirmPassword && isValid;

  const onSubmit = async (data: ChangePasswordSchemaType) => {
    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      // Call password change endpoint
      await httpService.patch("/api/users/password/change", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmationPassword: data.confirmPassword,
      });

      reset();
      setSuccessMessage("Password changed successfully!");
    } catch (error: any) {
      setErrorMessage(error.message || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white border border-border-gray rounded-none p-6 max-w-96">
      <div className="mb-6">
        <h3 className="text-base font-bold uppercase tracking-wide">CHANGE PASSWORD</h3>
        <p className="text-sm text-gray-600 mt-2">Ensure Your Account is using a Strong Password</p>
      </div>

      {successMessage && (
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
          className="mb-4"
        />
      )}

      {errorMessage && (
        <ErrorMessage
          message={errorMessage}
          onDismiss={() => setErrorMessage("")}
          className="mb-4"
        />
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-0 max-w-md">
        <div className="relative pb-5">
          <Label htmlFor="currentPassword" className="text-sm font-medium text-black mb-2 block">
            Current Password
          </Label>
          <div className="relative">
            <Input
              id="currentPassword"
              type={showCurrentPassword ? "text" : "password"}
              placeholder="Enter Current Password"
              {...register("currentPassword")}
              error={!!errors.currentPassword}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none pr-10 placeholder:text-gray-500"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              disabled={isLoading}
              tabIndex={-1}
            >
              {showCurrentPassword ? (
                <EyeClosedIcon className="h-4 w-4" />
              ) : (
                <EyeOpenIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.currentPassword && (
            <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
              {errors.currentPassword.message}
            </p>
          )}
        </div>

        <div className="relative pb-5">
          <Label htmlFor="newPassword" className="text-sm font-medium text-black mb-2 block">
            New Password
          </Label>
          <div className="relative">
            <Input
              id="newPassword"
              type={showNewPassword ? "text" : "password"}
              placeholder="Enter New Password"
              {...register("newPassword")}
              error={!!errors.newPassword}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none pr-10 placeholder:text-gray-500"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              disabled={isLoading}
              tabIndex={-1}
            >
              {showNewPassword ? (
                <EyeClosedIcon className="h-4 w-4" />
              ) : (
                <EyeOpenIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.newPassword && (
            <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
              {errors.newPassword.message}
            </p>
          )}
        </div>

        <div className="relative pb-5">
          <Label htmlFor="confirmPassword" className="text-sm font-medium text-black mb-2 block">
            Confirm Password
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm New Password"
              {...register("confirmPassword")}
              error={!!errors.confirmPassword}
              className="w-full h-12 bg-disabled-bg border-border-gray rounded-none pr-10 placeholder:text-gray-500"
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              disabled={isLoading}
              tabIndex={-1}
            >
              {showConfirmPassword ? (
                <EyeClosedIcon className="h-4 w-4" />
              ) : (
                <EyeOpenIcon className="h-4 w-4" />
              )}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-xs text-red-500 absolute left-0 top-[calc(100%-1.25rem)]">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <div className="pt-4">
          <Button
            type="submit"
            disabled={isLoading || !isFormValid}
            className="px-8 h-12 w-full bg-primary-blue hover:bg-primary-blue/90 text-white rounded-none text-sm disabled:opacity-50"
          >
            {isLoading ? "Changing password..." : "Change Password"}
          </Button>
        </div>
      </form>
    </div>
  );
};
